import React, { useMemo, useState } from 'react';
import { Box, Typography, Checkbox } from '@mui/material';
import {
  RequestFormFormControlLabel,
  RequestFormInput,
  RequestFormInputWrapper,
  SubmitButton,
  TextFieldFormInput,
  TypographyBody,
  Warning,
} from '@app/ui/pages/landing-page/request-form/request-form.styles';

import { Formik, Form } from 'formik';
import {
  hasError,
  validate,
} from '@app/ui/pages/landing-page/request-form/request-form.services';
import {
  RequestFormData,
  RequestFormErrors,
} from '@app/ui/pages/landing-page/request-form/request-form.types';
import { Autocomplete } from '@mui/material';
import { useFetchDaDataSuggestAddress } from '@app/api/dadata';
import { useSendConnectionRequest } from '@app/api/connectionRequest';
import { parsePhoneNumber } from '@app/lib/utils';
import { useDebounce } from '@app/lib/hooks/useDebounce';
import { getErrorMessage } from '@app/lib/errorHandling';
import { ConnectionRequestPayload, ConnectionRequestService, DaDataSuggestion } from '@app/api/types';

const DEFAULT_ERRORS = {
  name: undefined,
  email: undefined,
  phone: undefined,
  address: undefined,
};

export const RequestForm = () => {
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState<RequestFormErrors>(DEFAULT_ERRORS);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const [checkedRouter, setCheckedRouter] = useState(false);
  const [checkedTVBox, setCheckedTVBox] = useState(false);
  const [checkedOther, setCheckedOther] = useState(false);

  const [otherDescription, setOtherDescription] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Сохраняем полный объект addressData от DaData
  const [addressData, setAddressData] = useState<DaDataSuggestion | null>(null);

  // Используем debounce для оптимизации запросов к DaData (2 секунды)
  const debouncedAddress = useDebounce(address, 2000);

  const { data: daDataSuggestAddresses } = useFetchDaDataSuggestAddress(
    debouncedAddress || '',
    !!debouncedAddress && debouncedAddress.length >= 5
  );

  const sendConnectionRequestMutation = useSendConnectionRequest({
    onSuccess: () => {
      // Очищаем форму при успехе
      setName('');
      setAddress('');
      setPhone('');
      setOtherDescription('');
      setEmail('');
      setCheckedOther(false);
      setCheckedRouter(false);
      setCheckedTVBox(false);
      setAddressData(null);
      setSuccess(true);
      setDisabled(false);
      setErrorMessage('');
    },
    onError: (error) => {
      // Обрабатываем ошибку с user-friendly сообщением
      setErrorMessage(error);
      setDisabled(false);
    },
  });

  const addressObject = useMemo(() => {
    return (
      daDataSuggestAddresses &&
      daDataSuggestAddresses.suggestions.find((addressObj) => {
        return addressObj.value === address;
      })
    );
  }, [daDataSuggestAddresses, address]);

  return (
    <Formik
      initialValues={{
        name: '',
        email: '',
        phone: '',
        address: '',
      }}
      onSubmit={async () => {
        // Проверяем валидацию
        const formData: RequestFormData = {
          address,
          phone,
          name,
          email,
          checkedRouter,
          checkedTVBox,
          checkedOther,
          otherDescription,
        };

        if (hasError(formData)) {
          setErrors(validate(formData));
          return;
        }

        setDisabled(true);
        setErrors(DEFAULT_ERRORS);
        setErrorMessage('');
        setSuccess(false);

        // Маппим данные формы в формат API
        const services: ConnectionRequestService[] = [ConnectionRequestService.INTERNET];
        if (checkedRouter) services.push(ConnectionRequestService.ROUTER);
        if (checkedTVBox) services.push(ConnectionRequestService.TV_BOX);
        if (checkedOther) services.push(ConnectionRequestService.OTHER);

        const payload: ConnectionRequestPayload = {
          name,
          phone,
          email: email || undefined,
          address,
          addressData: addressData || undefined,
          services,
          otherDescription: checkedOther && otherDescription ? otherDescription : undefined,
        };

        // Отправляем заявку (обработка успеха и ошибок в колбэках mutation)
        sendConnectionRequestMutation.mutate(payload);
      }}
    >
      {() => {
        const hasErrors = Boolean(
          errors.name || errors.phone || errors.email || errors.address
        );

        return (
          <Form>
            <RequestFormInputWrapper
              justifyContent='space-between'
              display='flex'
              flexWrap='wrap'
            >
              <Box flex='1'>
                <Typography variant='h4'>
                  Заявка на подключение интернета
                </Typography>
                <TypographyBody variant='body2'>
                  Заполните форму для создания заявки и наш менеджер свяжется с
                  вами в ближайшее время.
                </TypographyBody>
                <TypographyBody variant='body2'>
                  Подключение происходит после согласования с менеджером.
                </TypographyBody>
              </Box>
              <Box flex='1' display='flex' flexDirection='column'>
                <RequestFormInputWrapper>
                  <RequestFormInput
                    disabled={disabled}
                    name='name'
                    placeholder='Имя'
                    error={errors.name}
                    value={name}
                    onChange={(e: any) => {
                      setName(e.target.value);
                    }}
                  />
                  {/* {errors.name && <p> {errors.name} </p>} */}
                  <RequestFormInput
                    disabled={disabled}
                    name='phone'
                    placeholder='Телефон'
                    error={errors.phone}
                    value={phone}
                    onChange={(e: any) => {
                      setPhone(parsePhoneNumber(e.target.value));
                    }}
                  />
                  {/* {errors.phone && <p> {errors.phone} </p>} */}
                </RequestFormInputWrapper>
                <Box flexDirection='column' display='flex'>
                  <RequestFormInput
                    disabled={disabled}
                    name='email'
                    placeholder='E-Mail. (Необязательно)'
                    value={email}
                    error={errors.email}
                    onChange={(e: any) => {
                      setEmail(e.target.value);
                    }}
                  />
                  <Autocomplete
                    disabled={disabled}
                    id='request-addr'
                    disablePortal
                    freeSolo
                    isOptionEqualToValue={(option, value) => option === value}
                    onInputChange={(event, value) => {
                      setAddress(value);
                      // Сбрасываем addressData при ручном вводе или изменении
                      if (!value || (addressData && value !== addressData.value)) {
                        setAddressData(null);
                      }
                    }}
                    onChange={(event, value) => {
                      // Сохраняем полный объект addressData при выборе из списка
                      if (value && daDataSuggestAddresses) {
                        const selectedAddress = daDataSuggestAddresses.suggestions.find(
                          (suggest) => suggest.value === value
                        );
                        if (selectedAddress) {
                          setAddressData(selectedAddress);
                          setAddress(selectedAddress.value);
                        }
                      } else {
                        setAddressData(null);
                      }
                    }}
                    value={address}
                    options={
                      daDataSuggestAddresses?.suggestions.map(
                        (suggest) => suggest.value
                      ) || []
                    }
                    getOptionLabel={(value) => value}
                    filterOptions={(option) => option}
                    renderInput={(params) => (
                      <Box
                        display='flex'
                        flexDirection='row'
                        alignItems='center'
                      >
                        <TextFieldFormInput
                          {...params}
                          name='address.fullAddress'
                          autoComplete='chrome-off'
                          value={address}
                          required
                          placeholder='Адрес'
                          error={!!errors.address}
                        />
                        {/* {addressObject && <SuccessIcon />} */}
                      </Box>
                    )}
                  />
                </Box>
                {hasErrors && (
                  <Warning severity='warning'>
                    {' '}
                    Выделенные поля обязательны для заполнения{' '}
                  </Warning>
                )}
                {errorMessage && (
                  <Warning severity='error'>
                    {' '}
                    {errorMessage}{' '}
                  </Warning>
                )}
                <Box>
                  <TypographyBody> Требуется дополнительное оборудование?</TypographyBody>
                  <Box>
                    <RequestFormInputWrapper>
                      <RequestFormFormControlLabel
                        control={
                          <Checkbox
                            color='primary'
                            checked={checkedRouter}
                            onChange={(event, checked) =>
                              setCheckedRouter(checked)
                            }
                          />
                        }
                        label='Роутер'
                        labelPlacement='end'
                        disabled={disabled}
                      />
                      <RequestFormFormControlLabel
                        control={
                          <Checkbox
                            color='primary'
                            checked={checkedTVBox}
                            onChange={(event, checked) => setCheckedTVBox(checked)}
                          />
                        }
                        label='ТВ приставка'
                        labelPlacement='end'
                        disabled={disabled}
                      />
                      <RequestFormFormControlLabel
                        control={
                          <Checkbox
                            color='primary'
                            checked={checkedOther}
                            onChange={(event, checked) =>
                              setCheckedOther(checked)
                            }
                          />
                        }
                        label='Другое'
                        labelPlacement='end'
                        disabled={disabled}
                      />
                    </RequestFormInputWrapper>
                    {checkedOther && (
                      <RequestFormInput
                        name='description'
                        placeholder='Описание'
                        value={otherDescription}
                        onChange={(e: any) =>
                          setOtherDescription(e.target.value)
                        }
                      />
                    )}
                    {!success && (
                      <SubmitButton type='submit' disabled={disabled}>
                        Оставить заявку на подключение
                      </SubmitButton>
                    )}
                    {success && (
                      <Warning severity='success'>
                        {' '}
                        Заявка отправлена. Скоро с вами свяжемся.{' '}
                      </Warning>
                    )}
                  </Box>
                </Box>
              </Box>
            </RequestFormInputWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
